import React, { useState, useEffect } from 'react';
import moment from 'moment';
import './../../App.css';
import { bookingService, talentService } from '../../api/apiService';
import { CalendarProvider, useCalendar } from '../../contexts/CalendarContext';
import Calendar from '../Calendar/Calendar';
import 'moment/locale/de';  // or any locale that uses day-month-year format
import CreateDummyOrder from './CreateDummyOrder';
import TalentsOnHoldList from './TalentsOnHoldList';
import { websocketService } from '../../services/websocketService';
import "../../styles/NewCalendar.css";

const TalentsOnHoldContent = ({ onEventsUpdate }) => {
    const [view, setView] = useState('list');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const { events, setEvents, currentDate } = useCalendar();
    const [originalEvents, setOriginalEvents] = useState([]);

    // Helper function to get current week number (ISO 8601)
    const getCurrentWeek = (date = new Date()) => {
        const targetDate = new Date(date);
        const start = new Date(targetDate.getFullYear(), 0, 1);
        const today = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate());
        
        // Set both dates to Thursday of their respective weeks
        // This ensures we get the correct ISO week number
        start.setDate(start.getDate() + (4 - (start.getDay() || 7)));
        today.setDate(today.getDate() + (4 - (today.getDay() || 7)));
        
        const weekNumber = Math.ceil((((today - start) / 86400000) + 1) / 7);
        return weekNumber;
    };

    // Get initial week from calendar's current date or current date
    const getInitialWeek = () => {
        if (currentDate) {
            return getCurrentWeek(currentDate);
        }
        return getCurrentWeek();
    };

    useEffect(() => {
        // Connect to WebSocket when the component mounts
        websocketService.connect();

        // Handle booking updates
        const handleBookingUpdate = (data) => {
            // console.log('Received booking update:', data);
            if (data.status === 'optionSolved' || data.status === 'talentOnHold') {
                fetchBookings();
            }
        };

        // Subscribe to booking updates
        websocketService.subscribe('booking', handleBookingUpdate);

        // Initial data fetch
        fetchBookings();

        // Cleanup on unmount
        return () => {
            websocketService.unsubscribe('booking', handleBookingUpdate);
            websocketService.disconnect();
        };
    }, []);

    const fetchTalent = async (talentId) => {
        try {
            if (!talentId) return 'No talent assigned';
            
            const response = await talentService.getTalentById(talentId);
            if (response && response.data && response.data.talent) {
                return response.data.talent.name || 'Unknown talent';
            }
            return 'Unknown talent';
        } catch (error) {
            console.error('Error fetching talent:', error);
            return 'Error loading talent';
        }
    }

    const fetchBookings = async () => {
        setIsLoading(true);
        setError(null);
        
        try {
            // Get current date in YYYY-MM-DD format for filter
            const today = moment().format('YYYY-MM-DD');
            const threeMonthsAhead = moment().add(3, 'months').format('YYYY-MM-DD');
            
            // Use the bookingService to get bookings with statuses optionSolved and talentOnHold
            const response = await bookingService.getAllBookings(
                1,  // page
                2000, // limit
                today, // from date
                threeMonthsAhead, // to date
                'optionSolved,talentOnHold' // specific statuses we want
            );
            
            if (!response || !response.data || !response.data.bookings) {
                throw new Error('Invalid response from API');
            }
            
            const bookings = response.data.bookings;
            // console.log('Fetched bookings:', bookings);
            // console.log('Sample booking structure:', bookings[0]);
            // console.log('Sample booking talent_id field:', bookings[0]?.talent_id);
            
            // Process bookings into events
            const processedEvents = await Promise.all(bookings.map(async (booking) => {
                // Debug logging for booking data
                // console.log('Processing booking:', booking.id, 'talent_id:', booking.talent_id);
                
                // For each booking, get the talent name
                let talentName = 'No talent assigned';
                const talentId = booking.talent_id ? String(booking.talent_id).trim() : null;
                
                if (talentId && talentId !== 'pending' && talentId !== '' && talentId !== 'null' && talentId !== 'undefined') {
                    try {
                        const talentResponse = await talentService.getTalentById(talentId);
                        // console.log('Talent API response for ID', talentId, ':', talentResponse);
                        // console.log('Full response structure:', JSON.stringify(talentResponse, null, 2));
                        
                        // Check multiple possible response structures - prioritize Name (uppercase) first
                        if (talentResponse?.data?.Name) {
                            talentName = talentResponse.data.Name;
                        } else if (talentResponse?.data?.name) {
                            talentName = talentResponse.data.name;
                        } else if (talentResponse?.data?.talent?.Name) {
                            talentName = talentResponse.data.talent.Name;
                        } else if (talentResponse?.data?.talent?.name) {
                            talentName = talentResponse.data.talent.name;
                        } else if (talentResponse?.Name) {
                            talentName = talentResponse.Name;
                        } else if (talentResponse?.name) {
                            talentName = talentResponse.name;
                        } else if (talentResponse?.talent?.Name) {
                            talentName = talentResponse.talent.Name;
                        } else if (talentResponse?.talent?.name) {
                            talentName = talentResponse.talent.name;
                        } else {
                            // console.warn(`No name found for talent ID: ${talentId}`, 'Response:', talentResponse);
                            // Try to get any available name field
                            const responseStr = JSON.stringify(talentResponse);
                            if (responseStr.includes('"name"') || responseStr.includes('"Name"')) {
                                // console.log('Response contains name field but structure is different:', responseStr);
                            }
                        }
                        
                        // console.log('Final talent name for booking', booking.id, ':', talentName);
                    } catch (error) {
                        console.error(`Error fetching talent ${talentId}:`, error);
                        console.error('Error details:', error.response?.data || error.message);
                    }
                } else {
                    // console.log('Booking', booking.id, 'has no valid talent_id:', talentId, '(original:', booking.talent_id, ')');
                }
                
                // Extract client information
                const clientName = typeof booking.client === 'object' ? 
                    booking.client?.name || booking.client?.label || 'Unknown client' : 
                    booking.client || 'Unknown client';
                
                // Extract position information
                const positionName = typeof booking.position === 'object' ? 
                    booking.position?.name || booking.position?.label || '' : 
                    booking.position || '';
                
                // Create start and end dates from booking data
                const startDate = booking.start_date ? new Date(booking.start_date) : new Date();
                const endDate = booking.end_date ? new Date(booking.end_date) : startDate;

                // Ensure dates are valid
                if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
                    // console.warn(`Invalid dates for booking ${booking.id}:`, { start_date: booking.start_date, end_date: booking.end_date });
                }

                return {
                    id: booking.id,
                    bookingId: booking.id,
                    title: booking.title || `Booking for ${talentName}`,
                    start: startDate,
                    end: endDate,
                    allDay: true,
                    status: booking.status,

                    // Talent information
                    talent: talentName,
                    talent_id: booking.talent_id,
                    talent_name: talentName,
                    
                    // Client and position
                    client: clientName,
                    position: positionName,
                    
                    // Additional details
                    set: booking.set || '',
                    kpi: booking.kpi || 0,
                    option: booking.option || '',
                    start_time: booking.start_time || '',
                    end_time: booking.end_time || '',
                    start_date: booking.start_date,
                    end_date: booking.end_date,
                    
                    // Location information
                    studio: booking.studio || '',
                    location: booking.location || '',
                    
                    // Additional fields
                    sonstige_info: booking.sonstige_info || '',
                    created_at: booking.created_at || '',
                    created_by: booking.created_by || '',
                    proposed_by: booking.proposed_by || '',
                };
            }));

            // Filter out any events with invalid dates
            const validEvents = processedEvents.filter(event => 
                !isNaN(event.start.getTime()) && !isNaN(event.end.getTime())
            );

            // console.log('Processed events:', validEvents);
            // console.log('Sample event with talent info:', validEvents[0]);
            
            // Set both states with the same processed events
            setOriginalEvents(validEvents);
            // For calendar view, set the CalendarProvider events
            setEvents(validEvents);
            setIsLoading(false);
        } catch (error) {
            console.error('Error fetching bookings:', error);
            setError(`Failed to load bookings: ${error.message}`);
            setIsLoading(false);
            setEvents([]);
            setOriginalEvents([]);
        }
    };

    const handleEventClick = (event) => {
        // Handle event click - you might want to navigate to a booking detail page or implement another action
        // console.log('Event clicked:', event);
    };

    const switchView = (viewType) => {
        setView(viewType);
    };

    // Filter for our specific statuses if needed (already filtered from the API, but can add additional filtering here)
    // Use originalEvents for list view to avoid CalendarProvider initialization issues
    const filteredEvents = originalEvents.filter(event => 
        event.status === 'optionSolved' || event.status === 'talentOnHold'
    );

    return (
        <div className="content-wrapper page">
            <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '20px'}}>
                <h1>Talents on Hold</h1>
                <div>
                    <button 
                        className={view === 'create-dummy' ? 'big-button-active' : 'big-button'} 
                        onClick={() => switchView('create-dummy')}
                    >
                        Create Talents on Hold
                    </button>
                    <button 
                        className={view === 'calendar' ? 'big-button' : 'big-button-active'} 
                        onClick={() => switchView('list')}
                    >
                        List View
                    </button>
                    <button 
                        className={view === 'calendar' ? 'big-button-active' : 'big-button'} 
                        onClick={() => switchView('calendar')}
                    >
                        Calendar View
                    </button>
                </div>
            </div>
            
            <div style={{ height: "100%", marginTop: '20px', width: '100%'}}>
                {isLoading && (
                    <div style={{textAlign: 'center', padding: '20px'}}>
                        Loading bookings...
                    </div>
                )}
                
                {error && (
                    <div style={{ color: 'red', textAlign: 'center', padding: '20px' }}>
                        {error}
                    </div>
                )}
                
                {!isLoading && !error && (
                    <>
                        {view === 'calendar' ? (
                            <Calendar
                                onEventClick={handleEventClick}
                                initialFilters={{
                                    bookingStatus: 'optionSolved,talentOnHold'
                                }}
                                events={filteredEvents} // Pass the pre-filtered events directly
                            />
                        ) : view === 'list' ? (
                            filteredEvents.length > 0 ? (
                                <TalentsOnHoldList 
                                    events={filteredEvents} // Use local originalEvents for list
                                    initialWeek={getInitialWeek()}
                                />
                            ) : (
                                <div style={{textAlign: 'center', padding: '20px'}}>
                                    No talents on hold found.
                                </div>
                            )
                        ) : (
                            <CreateDummyOrder />
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

// Wrap with CalendarProvider to access the calendar context
const TalentsOnHold = () => {
    const [filteredEventsForProvider, setFilteredEventsForProvider] = useState([]);
    
    return (
        <CalendarProvider initialEvents={filteredEventsForProvider}>
            <TalentsOnHoldContent onEventsUpdate={setFilteredEventsForProvider} />
        </CalendarProvider>
    );
};

export default TalentsOnHold;
